/**
 * Medical information on file — which athletes have none, and a family's
 * answer ("here it is" or "nothing to report").
 *
 * Failure shape mirrors success shape: every function here resolves the
 * response BODY (or its data) on success, so on failure it REJECTS with the
 * axios error (the backend's own `{ error }` body stays on
 * `err.response.data`). Callers read the message with
 * `medicalInfoErrorMessage(err, fallback)`.
 */
import axios from "./axiosInstance";
import config from "./config";

const BASE_URL = config.BASE_URL;

/** One of the signed-in family's athletes with no medical information on file. */
export interface MyAthleteMissingMedicalInfo {
  athlete_id: string;
  first_name: string;
  last_name: string;
}

/** A row of the staff list. Never carries medical text. */
export interface AthleteMissingMedicalInfo extends MyAthleteMissingMedicalInfo {
  membership_id: string;
  family_name: string;
}

export interface MissingMedicalInfoResponse {
  data: AthleteMissingMedicalInfo[];
  total_active_athletes?: number;
}

export type MedicalInfoAnswer =
  | { nothing_to_report: true }
  | { medical_information?: string; medication?: string };

const authHeaders = (token: string) => ({
  "Content-Type": "application/json",
  authorization: `Bearer ${token}`,
});

/** The backend's own words when it sent any, else a plain fallback. */
export const medicalInfoErrorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.error || fallback;

// ── Staff ─────────────────────────────────────────────────────────────

export const getAthletesMissingMedicalInfo = async (token: string): Promise<MissingMedicalInfoResponse> => {
  const response = await axios.get(`${BASE_URL}/medical-info/missing`, { headers: authHeaders(token) });
  return response.data as MissingMedicalInfoResponse;
};

/**
 * What the staff athlete page shows about medical information on file.
 * Never carries the medical text itself.
 */
export interface AthleteMedicalStatus {
  athlete_id: string;
  /** Something is written under Medical Information or Medication. */
  has_written_information: boolean;
  on_file: boolean;
  /** Set when "nothing to report" was confirmed: when, by whom, in what capacity. */
  nothing_to_report: { at: string; source: "family" | "staff"; by_name: string | null } | null;
  /** False when this club's database cannot record a staff confirmation yet. */
  staff_confirmation_available: boolean;
}

export const getAthleteMedicalStatus = async (token: string, athleteId: string): Promise<AthleteMedicalStatus> => {
  const response = await axios.get(`${BASE_URL}/medical-info/athlete/${encodeURIComponent(athleteId)}`, {
    headers: authHeaders(token),
  });
  return response.data?.data as AthleteMedicalStatus;
};

/**
 * Staff record "nothing to report" on the family's behalf. Saved as a STAFF
 * confirmation (who and when), never as if the family had confirmed it.
 */
export const staffConfirmNothingToReport = async (token: string, athleteId: string): Promise<void> => {
  await axios.post(
    `${BASE_URL}/medical-info/athlete/${encodeURIComponent(athleteId)}/staff-nothing-to-report`,
    {},
    { headers: authHeaders(token) }
  );
};

/** Take back a staff confirmation. A family's own confirmation cannot be removed this way. */
export const staffUndoNothingToReport = async (token: string, athleteId: string): Promise<void> => {
  await axios.delete(`${BASE_URL}/medical-info/athlete/${encodeURIComponent(athleteId)}/staff-nothing-to-report`, {
    headers: authHeaders(token),
  });
};

// ── Signed-in parent (always their own family) ────────────────────────

export const getMyAthletesMissingMedicalInfo = async (token: string): Promise<MyAthleteMissingMedicalInfo[]> => {
  const response = await axios.get(`${BASE_URL}/medical-info/mine`, { headers: authHeaders(token) });
  return (response.data?.data as MyAthleteMissingMedicalInfo[]) || [];
};

export const answerMedicalInfo = async (token: string, athleteId: string, answer: MedicalInfoAnswer): Promise<void> => {
  await axios.post(`${BASE_URL}/medical-info/mine/${encodeURIComponent(athleteId)}`, answer, {
    headers: authHeaders(token),
  });
};
